import type { TableAction, TableActionGroup } from "@shared/types/data-table"
import type { BillingSchedule } from "@features/billing-schedules/types/billing-schedule.types"
import { type BaseActionHandlers } from "./base-actions"
import { buildGroupedRowActions } from "./build-grouped-actions"

interface BillingScheduleSpecificHandlers {
  onViewSchedule?: (schedule: BillingSchedule) => void
  onDuplicateSchedule?: (schedule: BillingSchedule) => void
  onArchiveSchedule?: (schedule: BillingSchedule) => void
  onBulkArchive?: (scheduleIds: string[]) => void
  onBulkDuplicate?: (scheduleIds: string[]) => void
  onActivate?: (schedule: BillingSchedule) => void
  onPause?: (schedule: BillingSchedule) => void
  onCancel?: (schedule: BillingSchedule) => void
  onViewCustomer?: (schedule: BillingSchedule) => void
  onCopyScheduleId?: (schedule: BillingSchedule) => void
  onEdit?: (schedule: BillingSchedule) => void
}

export type BillingScheduleActionHandlers = BaseActionHandlers<BillingSchedule> & BillingScheduleSpecificHandlers

export const getBillingScheduleRowActions = (handlers: BillingScheduleActionHandlers = {}): TableActionGroup<BillingSchedule>[] =>
  buildGroupedRowActions<BillingSchedule>({
    view: [
      {
        key: "viewSchedule",
        label: "View schedule",
        onClick: (schedule) => handlers.onViewSchedule?.(schedule),
      },
    ],
    actions: [
      {
        key: "edit",
        label: "Edit schedule",
        onClick: (schedule) => handlers.onEdit?.(schedule),
      },
      {
        key: "activate",
        label: "Activate",
        onClick: (schedule) => handlers.onActivate?.(schedule),
        condition: (schedule) => schedule.status === 'draft' || schedule.status === 'archived',
      },
      {
        key: "pause",
        label: "Pause",
        onClick: (schedule) => handlers.onPause?.(schedule),
        condition: (schedule) => schedule.status === 'active',
      },
      {
        key: "duplicateSchedule",
        label: "Duplicate schedule",
        onClick: (schedule) => handlers.onDuplicateSchedule?.(schedule),
      },
      {
        key: "copyId",
        label: "Copy schedule ID",
        onClick: (schedule) => handlers.onCopyScheduleId?.(schedule),
      },
      {
        key: "cancel",
        label: "Cancel schedule",
        onClick: (schedule) => handlers.onCancel?.(schedule),
        condition: (schedule) => schedule.status === 'active',
        variant: "destructive" as const,
      },
      {
        key: "archiveSchedule",
        label: "Archive schedule",
        onClick: (schedule) => handlers.onArchiveSchedule?.(schedule),
        condition: (schedule) => schedule.status !== 'archived',
        variant: "destructive" as const,
      },
    ],
    links: [
      {
        key: "viewCustomer",
        label: "View customer",
        onClick: (schedule) => handlers.onViewCustomer?.(schedule),
        condition: (schedule) => !!schedule.customerId,
      },
    ],
  })

export const getBillingScheduleActions = (handlers: BillingScheduleActionHandlers = {}) => {
  return {
    bulkActions: [
      {
        key: "duplicate",
        label: "Duplicate",
        onClick: (ids: string[]) => handlers.onBulkDuplicate?.(ids),
        disabled: (ids: string[]) => ids.length === 0,
      },
      {
        key: "archive",
        label: "Archive",
        onClick: (ids: string[]) => handlers.onBulkArchive?.(ids),
        disabled: (ids: string[]) => ids.length === 0,
      },
    ],
    rowActions: getBillingScheduleRowActions(handlers),
  }
}

export const getBillingScheduleBulkActions = (handlers: BillingScheduleActionHandlers = {}) =>
  getBillingScheduleActions(handlers).bulkActions

export const getBillingSchedulePanelActions = (handlers: BillingScheduleActionHandlers = {}): TableActionGroup<BillingSchedule>[] =>
  buildGroupedRowActions<BillingSchedule>({
    actions: [
      {
        key: "duplicateSchedule",
        label: "Duplicate schedule",
        onClick: (schedule) => handlers.onDuplicateSchedule?.(schedule),
      },
      {
        key: "archiveSchedule",
        label: "Archive schedule",
        onClick: (schedule) => handlers.onArchiveSchedule?.(schedule),
        condition: (schedule) => schedule.status !== 'archived',
        variant: "destructive" as const,
      },
    ],
  })

export const getBillingScheduleHoverActions = (handlers: BillingScheduleActionHandlers = {}): TableAction<BillingSchedule>[] => [
  {
    key: "viewSchedule",
    label: "View schedule",
    onClick: (schedule: BillingSchedule) => handlers.onViewSchedule?.(schedule),
  },
]
